'use client';

import { useEffect, useState } from 'react';
import { useMediaSource, type MediaItem } from '@akademi/site-builder-runtime';
import { prettify } from './prettify';

/**
 * Three-state row fetcher specific to {@link MediaSource} (Cinema8-
 * backed content). Sibling of {@link useLearningRow} for media-side
 * rails (VideoRow, ShortsRail, RankedShowcase).
 */
export type ContentRowState =
  | { kind: 'loading'; label: string }
  | { kind: 'loaded'; label: string; items: MediaItem[]; stale: boolean }
  | { kind: 'empty'; label: string };

export interface UseContentRowOpts {
  /** Source slug passed to {@code MediaSource.videos.fetchSource}. */
  filter: string;
  limit?: number;
  authorTitle?: string;
}

export function useContentRow(opts: UseContentRowOpts): ContentRowState {
  const { filter, limit = 12, authorTitle } = opts;
  const media = useMediaSource();

  const [state, setState] = useState<ContentRowState>({
    kind: 'loading',
    label: authorTitle ?? prettify(filter),
  });

  useEffect(() => {
    if (!media) {
      setState({ kind: 'empty', label: authorTitle ?? prettify(filter) });
      return;
    }
    const controller = new AbortController();
    setState({ kind: 'loading', label: authorTitle ?? prettify(filter) });

    (async () => {
      try {
        const res = await media.videos.fetchSource({
          source: filter,
          limit,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const label = authorTitle || res?.label || prettify(filter);
        if (!res || res.items.length === 0) {
          setState({ kind: 'empty', label });
          return;
        }
        setState({ kind: 'loaded', label, items: res.items, stale: false });
      } catch (e) {
        if (controller.signal.aborted) return;
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setState({ kind: 'empty', label: authorTitle ?? prettify(filter) });
      }
    })();

    return () => controller.abort();
  }, [filter, limit, authorTitle, media]);

  return state;
}
